import * as wjcGrid from '@mescius/wijmo.grid';
import * as wjcGridXlsx from '@mescius/wijmo.grid.xlsx';
import * as wjcOlap from '@mescius/wijmo.olap';
import * as wjcXlsx from '@mescius/wijmo.xlsx';

/**
 * PivotGrid を xlsx に出力する (旧 kobe-ac.app と同じ結果になるように)。
 *
 * Wijmo 5.20242 の FlexGridXlsxConverter は結合範囲を MergeManager の内部 _getMergedRange から取るため、
 * PivotGrid 専用の結合 (見出しのみ) を経由せず、allowMerging=All の PivotGrid では値セルまで
 * 「隣と同じ値」で結合される。結合先の値は出力されず空欄になる (例: 参加費の歩銭 0 が先頭行だけ残る)。
 * 出力中だけ公開 getMergedRange (PivotGrid 用) に差し替えて見出しのみ結合させる。
 *
 * legacyStyle=true は旧 kobe-ac.app 精算書の出力 (旧画面の表示スタイルをそのまま書き出したもの) に合わせる。
 */
export function savePivotXlsx(grid: wjcGrid.FlexGrid, filename: string, legacyStyle = false): void {
    const mm: any = (grid as any)._mrgMgr;
    const original = mm ? mm._getMergedRange : null;
    if (original) {
        mm._getMergedRange = function (panel: wjcGrid.GridPanel, r: number, c: number, clip = true) {
            if (panel.grid.collectionView instanceof wjcOlap.PivotCollectionView) {
                return mm.getMergedRange(panel, r, c, false);
            }
            return original.apply(mm, arguments);
        };
    }
    let workbook: wjcXlsx.Workbook;
    try {
        // ファイル名を渡さなければ Workbook を同期で組み立てて返す (保存はしない)
        workbook = wjcGridXlsx.FlexGridXlsxConverter.saveAsync(grid,
            {includeColumnHeaders: true, includeRowHeaders: true, includeCellStyles: false});
    } finally {
        if (original) mm._getMergedRange = original;
    }
    if (legacyStyle) {
        applyLegacyStyle(grid, workbook);
    }
    workbook.saveAsync(filename);
}

// 旧 kobe-ac.app (Lato・灰色見出し・総計は水色) の配色
const FONT = 'Lato';
const HEADER_COLOR = '#444444';
const HEADER_FILL = '#eaeaea';
const CELL_FILL = '#ffffff';
const TOTAL_FILL = '#d9edf7';
const BORDER_COLOR = '#c6c6c6';
const ROW_HEIGHT = 28;
const VALUE_WIDTH = 112;

function makeStyle(bold: boolean, color: string, fill: string, right: boolean, format?: string): wjcXlsx.WorkbookStyle {
    const style = new wjcXlsx.WorkbookStyle();
    style.font = new wjcXlsx.WorkbookFont();
    style.font.family = FONT;
    style.font.bold = bold;
    style.font.color = color;
    style.fill = new wjcXlsx.WorkbookFill();
    style.fill.color = fill;
    style.borders = new wjcXlsx.WorkbookBorder();
    // 塗りのあるセルは Wijmo が四辺に既定の罫線を補うため、旧と同じく左・上は明示的に無しにする
    style.borders.left = new wjcXlsx.WorkbookBorderSetting();
    style.borders.left.style = wjcXlsx.BorderStyle.None;
    style.borders.top = new wjcXlsx.WorkbookBorderSetting();
    style.borders.top.style = wjcXlsx.BorderStyle.None;
    style.borders.right = new wjcXlsx.WorkbookBorderSetting();
    style.borders.right.style = wjcXlsx.BorderStyle.Thin;
    style.borders.right.color = BORDER_COLOR;
    style.borders.bottom = new wjcXlsx.WorkbookBorderSetting();
    style.borders.bottom.style = wjcXlsx.BorderStyle.Thin;
    style.borders.bottom.color = BORDER_COLOR;
    if (right) style.hAlign = wjcXlsx.HAlign.Right;
    if (format) style.format = format;
    return style;
}

function applyLegacyStyle(grid: wjcGrid.FlexGrid, workbook: wjcXlsx.Workbook): void {
    const sheet = workbook.sheets[0];
    const headerRows = grid.columnHeaders.rows.length;
    const headerCols = grid.rowHeaders.columns.length;
    const engine = (grid as wjcOlap.PivotGrid).engine;
    // 総計は最終行と、値フィールド数ぶんの最終列
    const totalRow = engine && engine.showColumnTotals != wjcOlap.ShowTotals.None ? sheet.rows.length - 1 : -1;
    const totalCols = engine && engine.showRowTotals != wjcOlap.ShowTotals.None ? engine.valueFields.length : 0;
    const colCount = headerCols + grid.columns.length;

    for (let r = 0; r < sheet.rows.length; r++) {
        const row = sheet.rows[r];
        if (row == null) continue;
        row.height = ROW_HEIGHT;
        for (let c = 0; c < colCount; c++) {
            let cell = row.cells[c];
            if (cell == null) {
                cell = new wjcXlsx.WorkbookCell();
                row.cells[c] = cell;
            }
            const format = cell.style ? cell.style.format : undefined;
            if (r < headerRows) {
                // 列見出し。左上 (行フィールド見出しの上) は旧と同じく空欄・結合なし
                if (r < headerRows - 1 && c < headerCols) {
                    cell.value = null;
                    cell.colSpan = 1;
                }
                cell.style = makeStyle(true, HEADER_COLOR, HEADER_FILL, c >= headerCols);
            } else if (c < headerCols) {
                // 行見出し (顧客ID・顧客名)。顧客ID は文字列で出るので数値に戻す (旧と同じく 1,000 以上は桁区切りの文字列のまま)
                if (typeof cell.value === 'string' && /^\d+$/.test(cell.value)) {
                    cell.value = Number(cell.value);
                }
                cell.style = makeStyle(true, HEADER_COLOR, HEADER_FILL, false);
            } else if (r === totalRow || c >= colCount - totalCols) {
                cell.style = makeStyle(true, '#000000', TOTAL_FILL, true, format);
            } else {
                cell.style = makeStyle(false, '#000000', CELL_FILL, true, format);
            }
        }
    }
    for (let c = headerCols; c < colCount; c++) {
        if (sheet.columns[c] == null) sheet.columns[c] = new wjcXlsx.WorkbookColumn();
        sheet.columns[c].width = VALUE_WIDTH;
    }
}
